import React, { useState, useEffect } from 'react';

/**
 * CompanyLogo component with cascading fallback sources
 * Tries multiple logo APIs before showing ticker initials
 */
export default function CompanyLogo({ ticker, size = 'md', className = '' }) {
  const [currentSource, setCurrentSource] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [key, setKey] = useState(0); // Force re-render when source changes

  // Reset when ticker changes
  useEffect(() => {
    setCurrentSource(0);
    setImageError(false);
    setKey(prev => prev + 1);
  }, [ticker]);

  // Define size classes
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-16 h-16'
  };

  const textSizes = {
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-2xl'
  };

  const sizeClass = sizes[size] || sizes.md;
  const textSizeClass = textSizes[size] || textSizes.md;
  const padding = size === 'sm' ? 'p-0.5' : 'p-1';

  // Logo sources in priority order
  // Yahoo Finance has the best coverage for stock logos
  const logoSources = [
    `https://storage.googleapis.com/iex/api/logos/${ticker}.png`, // IEX Cloud (good coverage)
    `https://financialmodelingprep.com/image-stock/${ticker}.png`, // FMP API
    `https://eodhistoricaldata.com/img/logos/US/${ticker}.png`, // EOD Historical Data
  ];

  const handleImageError = (e) => {
    console.log(`Logo failed for ${ticker}, source ${currentSource}: ${logoSources[currentSource]}`);

    // Try next source
    if (currentSource < logoSources.length - 1) {
      setCurrentSource(currentSource + 1);
      setKey(prev => prev + 1); // Force img to reload with new src
    } else {
      // All sources failed, show fallback
      setImageError(true);
    }
  };

  const handleImageLoad = () => {
    console.log(`Logo loaded successfully for ${ticker} from source ${currentSource}`);
    setImageError(false);
  };

  // Show fallback if all sources failed
  if (imageError || currentSource >= logoSources.length) {
    return (
      <div className={`flex-shrink-0 ${className}`}>
        <div className={`${sizeClass} rounded-md bg-blue-100 flex items-center justify-center`}>
          <span className={`${textSizeClass} font-bold text-blue-700`}>
            {ticker.substring(0, 2).toUpperCase()}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-shrink-0 ${className}`}>
      <img
        key={key}
        src={logoSources[currentSource]}
        alt={ticker}
        className={`${sizeClass} rounded-md object-contain bg-white border border-gray-200 ${padding}`}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
      />
    </div>
  );
}
